import apiClient from './apiClient';
import { 
  mockStats, 
  mockDistribution, 
  mockTimeline, 
  mockImages,
  mockTopCategories 
} from './mockData';

// Flag to use mock data (set to false when backend is ready)
const USE_MOCK = false;

// Simulate API delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Stats API
export const getStats = async () => {
  if (USE_MOCK) {
    await delay(500);
    return mockStats;
  }
  const response = await apiClient.get('/stats');
  return response.data;
};

// Search API (keyword)
export const searchImages = async (query, filters = {}) => {
  if (USE_MOCK) {
    await delay(800);
    let results = [...mockImages];
    if (filters.category && filters.category !== 'All') results = results.filter(img => img.category === filters.category);
    return results;
  }
  const response = await apiClient.post('/search', { query, top_k: 50, filters });
  return response.data;
};

// Natural-language search via Gemini
export const nlSearch = async (query, top_k = 50) => {
  const response = await apiClient.post('/search/nl', { query, top_k });
  return response.data; // { results, parsed_filters, total }
};

// Photos API
export const getPhotos = async (params = {}) => {
  if (USE_MOCK) {
    await delay(600);
    return mockImages;
  }
  const response = await apiClient.get('/photos', { params });
  return response.data;
};

export const uploadPhotos = async (files, projectId) => {
  if (USE_MOCK) {
    await delay(1500);
    return { success: true, uploaded: files.length };
  }
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  formData.append('project_id', projectId);
  
  const response = await apiClient.post('/photos/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Analytics API
export const getAnalytics = async () => {
  if (USE_MOCK) {
    await delay(600);
    return {
      distribution: mockDistribution,
      timeline: mockTimeline,
      topCategories: mockTopCategories
    };
  }
  const response = await apiClient.get('/analytics');
  return response.data;
};

export const getDistribution = async () => {
  if (USE_MOCK) {
    await delay(400);
    return mockDistribution;
  }
  const response = await apiClient.get('/analytics/distribution');
  return response.data;
};

export const getTimeline = async (period = '7d') => {
  if (USE_MOCK) {
    await delay(400);
    return mockTimeline;
  }
  const response = await apiClient.get(`/analytics/timeline?period=${period}`);
  return response.data;
};

export const getTopCategories = async () => {
  if (USE_MOCK) {
    await delay(400);
    return mockTopCategories;
  }
  const response = await apiClient.get('/analytics/top-categories');
  return response.data;
};

// Export API — downloads a PDF damage report. Pass an empty/undefined
// imageIds to export all of the user's photos.
export const exportResults = async (imageIds, format = 'pdf') => {
  const response = await apiClient.post(
    '/export',
    { image_ids: imageIds && imageIds.length ? imageIds : null, format },
    { responseType: 'blob' }
  );

  // Trigger a browser download of the returned PDF.
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
  link.download = `hasar-raporu-${ts}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { success: true };
};
